import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'

/**
 * One of the caller's own settings, through `/users/me/settings/{code}`
 * (gated on `organization.read`). `present` tells an absent setting (404)
 * from a stored one: the digest invitation shows on absence only.
 */
export function useOwnSetting<T>(org: string, code: string) {
    const queryClient = useQueryClient()
    const queryKey = ['userSetting', org, 'me', code]

    const query = useQuery({
        queryKey,
        queryFn: async (): Promise<{ present: boolean; value: T | undefined }> => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/users/me/settings/{settingCode}', {
                params: { path: { orgCode: org, settingCode: code } },
            })
            if (response.error) {
                if (response.response.status === 404) return { present: false, value: undefined }
                throw new Error(`Failed to read the ${code} setting`)
            }
            const data = response.data as { value?: T } | undefined
            return { present: true, value: data?.value }
        },
        enabled: !!org,
        staleTime: 5 * 60 * 1000,
    })

    const save = useMutation({
        mutationFn: async (next: T) => {
            const response = await apiClient.PUT('/api/v1/organizations/{orgCode}/users/me/settings/{settingCode}', {
                params: { path: { orgCode: org, settingCode: code } },
                body: next as never,
            })
            if (response.error) throw new Error(`Failed to save the ${code} setting`)
            return next
        },
        onSuccess: (next) => queryClient.setQueryData(queryKey, { present: true, value: next }),
    })

    return {
        value: query.data?.value,
        present: query.data?.present ?? false,
        isLoading: query.isLoading,
        isError: query.isError,
        save: save.mutateAsync,
        isSaving: save.isPending,
        saveError: save.isError,
    }
}
